export const DEFAULT_PAGE_SIZE = 100;
export const MAX_PAGE_SIZE = 100;
export const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;
export const SEARCH_DEBOUNCE_MS = 250;
const OFFSET_CURSOR_PREFIX = "o:";

export type SortOrder = "asc" | "desc";

export type TableSnapshot = {
	pageIndex: number;
	pageSize: number;
	sort?: { id: string; desc: boolean };
	search: string;
};

export type OffsetListParams = {
	limit: number;
	offset: number;
	q?: string;
	sort_by?: string;
	order?: SortOrder;
};

export type CursorListParams = {
	limit: number;
	cursor?: string;
	q?: string;
	sort_by?: string;
	order?: SortOrder;
};

export type RowRange = {
	start: number;
	end: number;
	total: number;
};

export function clampPageSize(size: number): number {
	if (size <= 0) return DEFAULT_PAGE_SIZE;
	if (size > MAX_PAGE_SIZE) return MAX_PAGE_SIZE;
	return size;
}

export function encodeOffsetCursor(offset: number): string {
	return `${OFFSET_CURSOR_PREFIX}${offset}`;
}

export function decodeOffsetCursor(cursor: string): number | undefined {
	if (!cursor.startsWith(OFFSET_CURSOR_PREFIX)) return undefined;
	const n = Number(cursor.slice(OFFSET_CURSOR_PREFIX.length));
	if (!Number.isInteger(n) || n < 0) return undefined;
	return n;
}

function withSearchAndSort<T extends { q?: string; sort_by?: string; order?: SortOrder }>(
	params: T,
	state: TableSnapshot,
): T {
	const q = state.search.trim();
	if (q) params.q = q;
	if (state.sort) {
		params.sort_by = state.sort.id;
		params.order = state.sort.desc ? "desc" : "asc";
	}
	return params;
}

export function offsetListParams(state: TableSnapshot): OffsetListParams {
	const pageSize = clampPageSize(state.pageSize);
	const params: OffsetListParams = {
		limit: pageSize,
		offset: state.pageIndex * pageSize,
	};
	return withSearchAndSort(params, state);
}

export function cursorListParams(state: TableSnapshot): CursorListParams {
	const pageSize = clampPageSize(state.pageSize);
	const offset = state.pageIndex * pageSize;
	const params: CursorListParams = { limit: pageSize };
	if (offset > 0) params.cursor = encodeOffsetCursor(offset);
	return withSearchAndSort(params, state);
}

export function hasNextPage(offset: number, limit: number, total: number): boolean {
	return offset + limit < total;
}

export function pageCountFromTotal(total: number, pageSize: number): number {
	if (pageSize <= 0) return 1;
	return Math.max(1, Math.ceil(total / pageSize));
}

export function rowRange(
	pageIndex: number,
	pageSize: number,
	total: number,
): RowRange {
	if (total <= 0) return { start: 0, end: 0, total: 0 };
	const start = pageIndex * pageSize + 1;
	const end = Math.min(start + pageSize - 1, total);
	return { start, end, total };
}
